import {useEffect, useLayoutEffect, useRef, useState} from 'react';

import {List, ListItemButton, Typography} from '@mui/material';

import {orangeColors} from '../../layouts/theme/designSystem';
import {Option} from '../../types/shared-types';

interface PositionOrigin {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}

type AcceptedKey = 'Enter' | 'ArrowDown' | 'ArrowUp';

interface Props {
  options: Option[];
  textSearch: string;
  shouldOpen: boolean;
  onClick: (option: Option) => void;
  anchorElement?: HTMLElement | null;
  keyboardCode?: string;
}

export const UserMentionList = ({options, textSearch, shouldOpen, onClick, anchorElement, keyboardCode}: Props) => {
  const [availableOptions, setAvailableOptions] = useState<Option[]>([]);
  const [activeOption, setActiveOption] = useState<number>(0);
  const [position, setPosition] = useState<PositionOrigin>({});
  const listRef = useRef<HTMLUListElement>(null);
  const [currentKey, setCurrentKey] = useState<AcceptedKey | undefined>();

  const getActiveBgColor = (idx: number): string | undefined => {
    if (activeOption === idx) {
      return orangeColors[4];
    }
    return;
  };

  useEffect(() => {
    if (keyboardCode) {
      setCurrentKey(keyboardCode as AcceptedKey);
    }
  }, [keyboardCode]);

  useEffect(() => {
    setAvailableOptions(options);
  }, [options]);

  useEffect(() => {
    if (textSearch) {
      const matches = options.filter((option) => option.label.toLowerCase().includes(textSearch.toLowerCase()));
      setAvailableOptions(matches);
    }
  }, [textSearch, options]);

  useLayoutEffect(() => {
    if (!anchorElement || !shouldOpen) {
      return;
    }
    const list = listRef.current;
    const viewport = window?.visualViewport;
    if (!list || !viewport) {
      return;
    }

    const pos: PositionOrigin = {};
    const padding = 16;
    const anchorRect = anchorElement.getBoundingClientRect();

    // Using position: fixed — coordinates are viewport-relative, matching getBoundingClientRect()
    if (anchorRect.bottom + list.offsetHeight >= viewport.height - padding) {
      pos.bottom = viewport.height - anchorRect.top + padding;
    } else {
      pos.top = anchorRect.bottom;
    }

    if (anchorRect.left + list.offsetWidth >= viewport.width - padding) {
      pos.right = viewport.width - anchorRect.right;
    } else {
      pos.left = anchorRect.left;
    }

    setPosition(pos);
  }, [anchorElement, shouldOpen, availableOptions]);

  useEffect(() => {
    if (!currentKey || !listRef.current) return;
    setCurrentKey(undefined);
    const arrowKeys = ['ArrowUp', 'ArrowDown'];

    const toggleSelection = () => {
      let nextIndex;
      if (currentKey === 'ArrowUp') {
        nextIndex = activeOption === 0 ? availableOptions.length - 1 : activeOption - 1;
      } else {
        nextIndex = activeOption === availableOptions.length - 1 ? 0 : activeOption + 1;
      }
      setActiveOption(nextIndex);
    };

    if (currentKey === 'Enter' && !!availableOptions[activeOption]) {
      onClick(availableOptions[activeOption]);
      return;
    }

    if (arrowKeys.includes(currentKey)) {
      toggleSelection();
    }
  }, [currentKey, activeOption, onClick, availableOptions, listRef]);

  useEffect(() => {
    if (!availableOptions[activeOption]) return;

    const activeEntry = document.getElementById(availableOptions[activeOption].id);

    if (!activeEntry) return;

    activeEntry.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'smooth'});
  }, [activeOption, availableOptions]);

  return (
    <List
      id={'mention-list'}
      ref={listRef}
      tabIndex={0}
      sx={{
        width: 'min(320px, calc(100vw - 32px))',
        maxWidth: '320px',
        maxHeight: '250px',
        minWidth: '240px',
        overflowY: 'auto',
        backgroundColor: 'background.default',
        boxShadow: '1px 3px 8px 2px rgba(0,0,0,0.74)',
        borderRadius: '4px',
        display: shouldOpen ? 'block' : 'none',
        position: 'fixed',
        zIndex: 100,
        outline: 'none',
        ...position,
      }}
    >
      {!availableOptions.length ? (
        <ListItemButton>
          <Typography></Typography>
        </ListItemButton>
      ) : (
        availableOptions.map((option, idx) => (
          <ListItemButton
            id={option.id}
            className='user-mention-entry'
            sx={{
              backgroundColor: getActiveBgColor(idx),
            }}
            key={option.id}
            onClick={() => onClick(option)}
          >
            <Typography>{option.label}</Typography>
          </ListItemButton>
        ))
      )}
    </List>
  );
};
